import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1571335746824-742511d49bce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBjYXIlMjB0eXJlc3xlbnwwfHx8fDE3ODcwMzg1Njl8MA&ixlib=rb-4.1.0&q=85";
const INFLATOR =
  "https://images.pexels.com/photos/3807450/pexels-photo-3807450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  { text: "BUY 4", accent: false },
  { text: "CAR TYRES.", accent: false },
  { text: "GET REWARDED.", accent: true },
];

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 220]);
  const yFrame = useTransform(scrollY, [0, 900], [0, 120]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.img
        src={BG}
        alt=""
        aria-hidden
        style={{ y: yBg }}
        className="absolute inset-0 h-[120%] w-full object-cover opacity-50 grayscale"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" aria-hidden />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" aria-hidden />

      <motion.div
        style={{ y: yFrame }}
        className="absolute right-16 top-1/2 hidden w-[320px] -translate-y-1/2 xl:block"
        aria-hidden
      >
        <div className="clip-frame border-l-4 border-[#FF3B30]">
          <img src={INFLATOR} alt="" className="h-[420px] w-full object-cover grayscale" />
        </div>
        <p className="mt-4 text-right font-heading text-[10px] uppercase tracking-[0.3em] text-white/40">
          Free inflator · Worth ₹4,000
        </p>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-12 bg-[#FF3B30]" aria-hidden />
          <span className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#FF3B30]">
            01 · The Offer — Tyre Empire, Bengaluru
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-6xl uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-[#FF3B30]" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          Drive home 4 premium car tyres and pick your reward — a{" "}
          <span className="font-semibold text-[#FFCC00]">tyre inflator worth ₹4,000</span> or an{" "}
          <span className="font-semibold text-[#FFCC00]">Amazon shopping voucher worth ₹2,000</span>.
          <span className="mt-3 inline-block bg-[#FF3B30] px-2 py-1 font-heading text-sm font-bold uppercase tracking-[0.15em] text-white md:text-base">
            Exclusively for online registered customers
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href="#claim"
            data-testid="hero-claim-button"
            className="group flex items-center gap-3 bg-[#FF3B30] px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-[#FF3B30]"
          >
            Claim the Offer
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
          </a>
          <a
            href="#stores"
            data-testid="hero-stores-button"
            className="flex items-center gap-3 border border-white/20 px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
          >
            Find a Store
            <MapPin className="h-4 w-4" aria-hidden />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
